import type { DaycareSlot } from '~/stores/useDaycareStore'
import type { DaycareQueueEntry } from '~/utils/daycare'
import {
  mergeDaycareQueueEntries,
  mergeDaycareReserveFillPending,
  mergeDaycareSlotsPreservingProgress,
  reconcileDaycareSlotStarsAndDamage,
} from '../../../shared/daycare_merge'

/** Clé localStorage ; l’événement `storage` utilise la même chaîne. */
export const DAYCARE_PROGRESS_STORAGE_KEY_PREFIX = 'pokegrind-dc-progress-v1-'

export type DaycareProgressBackup = {
  ts: number
  slots: DaycareSlot[]
  lastDeposited: DaycareQueueEntry[]
  reserveQueue: DaycareQueueEntry[]
  reserveFillPending: number
}

function keyForUser(userId: number) {
  return `${DAYCARE_PROGRESS_STORAGE_KEY_PREFIX}${userId}`
}

function cloneSlot(s: DaycareSlot): DaycareSlot {
  const depositId =
    typeof s.depositId === 'string' && s.depositId.trim().length > 0 && s.depositId.length <= 64
      ? s.depositId.trim()
      : undefined
  return reconcileDaycareSlotStarsAndDamage({
    ...s,
    stars: Math.max(1, Math.min(5, Math.floor(Number(s.stars) || 1))),
    damageDealt: Math.max(0, Number(s.damageDealt) || 0),
    ...(depositId ? { depositId } : {}),
  })
}

function cloneQueueEntry(e: DaycareQueueEntry): DaycareQueueEntry {
  return {
    slug: e.slug,
    nameFr: e.nameFr,
    nameEn: e.nameEn,
    rarity: e.rarity,
    isShiny: Boolean(e.isShiny),
    stars: Math.max(1, Math.min(5, Math.floor(Number(e.stars) || 1))),
    cosmeticFormSlug: e.cosmeticFormSlug ?? null,
  }
}

export function buildDaycareProgressBackup(state: {
  slots: DaycareSlot[]
  lastDeposited: DaycareQueueEntry[]
  reserveQueue: DaycareQueueEntry[]
  reserveFillPending: number
}): DaycareProgressBackup {
  return {
    ts: Date.now(),
    slots: state.slots.map(cloneSlot),
    lastDeposited: state.lastDeposited.map(cloneQueueEntry),
    reserveQueue: state.reserveQueue.map(cloneQueueEntry),
    reserveFillPending: Math.max(0, Math.floor(Number(state.reserveFillPending) || 0)),
  }
}

export function persistDaycareProgressBackup(userId: number | undefined, backup: DaycareProgressBackup) {
  if (userId == null || userId <= 0) return
  try {
    localStorage.setItem(keyForUser(userId), JSON.stringify(backup))
  } catch {
    /* ignore */
  }
}

export function loadDaycareProgressBackup(userId: number): DaycareProgressBackup | null {
  if (userId <= 0) return null
  try {
    const raw = localStorage.getItem(keyForUser(userId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as DaycareProgressBackup
    if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.slots)) return null
    return {
      ts: Math.floor(Number(parsed.ts) || 0),
      slots: parsed.slots.map((s) => cloneSlot(s as DaycareSlot)),
      lastDeposited: Array.isArray(parsed.lastDeposited)
        ? parsed.lastDeposited.map((e) => cloneQueueEntry(e as DaycareQueueEntry))
        : [],
      reserveQueue: Array.isArray(parsed.reserveQueue)
        ? parsed.reserveQueue.map((e) => cloneQueueEntry(e as DaycareQueueEntry))
        : [],
      reserveFillPending: Math.max(0, Math.floor(Number(parsed.reserveFillPending) || 0)),
    }
  } catch {
    return null
  }
}

export function clearDaycareProgressBackup(userId: number | undefined) {
  if (userId == null || userId <= 0) return
  try {
    localStorage.removeItem(keyForUser(userId))
  } catch {
    /* ignore */
  }
}

/** Fusionne un snapshot local (autre onglet ou save différée) dans le store courant. */
export function mergeDaycareProgressBackupIntoStore(
  state: {
    slots: DaycareSlot[]
    lastDeposited: DaycareQueueEntry[]
    reserveQueue: DaycareQueueEntry[]
    reserveFillPending: number
    setSlots: (next: DaycareSlot[]) => void
    setLastDeposited: (next: DaycareQueueEntry[]) => void
    setReserveQueue: (next: DaycareQueueEntry[]) => void
    setReserveFillPending: (n: number) => void
  },
  backup: DaycareProgressBackup,
  maxSlots: number
): boolean {
  const mergedSlots = mergeDaycareSlotsPreservingProgress(state.slots, backup.slots, maxSlots)
  const mergedLast = mergeDaycareQueueEntries(state.lastDeposited, backup.lastDeposited, 24)
  const mergedReserve = mergeDaycareQueueEntries(state.reserveQueue, backup.reserveQueue, 30)
  const mergedPending = mergeDaycareReserveFillPending(state.reserveFillPending, backup.reserveFillPending)

  const slotsChanged =
    mergedSlots.length !== state.slots.length ||
    mergedSlots.some((s, i) => {
      const cur = state.slots[i]
      return (
        !cur ||
        s.slug !== cur.slug ||
        s.stars !== cur.stars ||
        s.damageDealt !== cur.damageDealt ||
        s.isShiny !== cur.isShiny
      )
    })
  const lastChanged = JSON.stringify(mergedLast) !== JSON.stringify(state.lastDeposited)
  const reserveChanged = JSON.stringify(mergedReserve) !== JSON.stringify(state.reserveQueue)
  const pendingChanged = mergedPending !== state.reserveFillPending

  if (!slotsChanged && !lastChanged && !reserveChanged && !pendingChanged) return false

  if (slotsChanged) state.setSlots(mergedSlots)
  if (lastChanged) state.setLastDeposited(mergedLast)
  if (reserveChanged) state.setReserveQueue(mergedReserve)
  if (pendingChanged) state.setReserveFillPending(mergedPending)
  return true
}

export function storageKeyForDaycareProgress(userId: number): string {
  return keyForUser(userId)
}
